/**
 * Maps score_card from combined daily_summary API to shape expected by scorecardDataTransformer (mmpay).
 * score_card is an array with one item: { orders, amount, refund, discounts, payout, payment, fees }.
 */
const toMetric = (value, change = 0) => ({
  value: value ?? 0,
  change: change ?? 0,
});

export const scoreCardFromDailySummaryResponse = response => {
  const card = response?.score_card?.[0];
  if (!card) {
    return {
      orders: toMetric(0),
      amount: toMetric(0),
      refund: toMetric(0),
      discounts: toMetric(0),
      payout: toMetric(0),
      payment: toMetric(0),
      fees: toMetric(0),
      bill_amount: toMetric(0),
    };
  }
  return {
    orders: toMetric(card.total_orders),
    amount: toMetric(card.amount),
    refund: toMetric(card.refund),
    discounts: toMetric(card.discounts),
    payout: toMetric(card.payout),
    payment: toMetric(card.payment),
    fees: toMetric(card.fees),
    bill_amount: toMetric(card.amount),
  };
};

/**
 * Formats MMPay daily summary API response for table consumption.
 * Maps overall_breakups and day_breakups to { overall, daily } with
 * row shape: bill_amount, total_orders, refund, discounts, fees, payment, payout.
 */
export const dailySummaryFormatter = payload => {
  const result = { overall: [], daily: [] };

  if (payload?.overall_breakups?.length) {
    result.overall = payload.overall_breakups.map(obj => ({
      key: obj.status,
      id: obj.status,
      status: obj.status,
      bill_amount: obj.bill_amount,
      total_orders: obj.total_orders,
      refund: obj.refund,
      discounts: obj.discounts,
      fees: obj.fees,
      payment: obj.payment,
    }));
  }

  if (payload?.day_breakups?.length) {
    result.daily = payload.day_breakups.map(obj => ({
      key: obj.date,
      id: obj.date,
      date: obj.date,
      type: 'main',
      name: obj.date,
      bill_amount: obj.bill_amount,
      total_orders: obj.total_orders,
      refund: obj.refund,
      discounts: obj.discounts,
      fees: obj.fees,
      payment: obj.payment,
      payout: obj.payout,
      children: (obj.break_up || []).map(child => ({
        key: `${obj.date}-${child.status}`,
        id: `${obj.date}-${child.status}`,
        type: 'child',
        name: child.status,
        bill_amount: child.bill_amount,
        total_orders: child.total_orders,
        refund: child.refund,
        discounts: child.discounts,
        fees: child.fees,
        payment: child.payment,
        payout: child.payout,
      })),
    }));
  }

  return result;
};

/**
 * Formats MMPay transactions API response for table consumption.
 * Preserves API field names; adds key and dateTime for display.
 */
export const transactionsFormatter = payload => {
  if (!Array.isArray(payload) || !payload.length) return [];
  return payload.map((obj, index) => ({
    key: obj.mm_id || `row-${index}`,
    id: obj.mm_id || `row-${index}`,
    dateTime: [obj.date, obj.time].filter(Boolean).join(' ') || '-',
    ...obj,
  }));
};
